/**
 * The typed API client. Every call goes through a route record in @ff/contracts: the path is built from the
 * route, the body is parsed with the route's body schema before it leaves, and the response is parsed with the
 * response schema before any screen sees it (docs/55 §2.3). Errors come back as ApiError with a contract code,
 * and the message shown is the contract's voice-linted message, never a raw error (docs/52 D10).
 */
import { CSRF_HEADER, CSRF_HEADER_VALUE, ERROR_CODES, buildPath, clientRoutes, type ErrorCode } from '@ff/contracts';
import type { z } from 'zod';

export type Routes = typeof clientRoutes;
export type RouteId = keyof Routes;
export type Res<K extends RouteId> = z.output<Routes[K]['response']>;
export type Body<K extends RouteId> = z.input<Routes[K]['body']>;
export type Query<K extends RouteId> = z.input<Routes[K]['query']>;
type ParamsOf<K extends RouteId> = Omit<z.input<Routes[K]['params']>, 't'>;

export interface CallInput<K extends RouteId> {
  params?: ParamsOf<K>;
  query?: Query<K>;
  body?: Body<K>;
  signal?: AbortSignal;
}

export class ApiError extends Error {
  code: ErrorCode | 'offline';
  status: number;
  field?: string;
  /**
   * The server's own words for a refusal, kept aside and never shown by default (docs/52 D10). Only a screen whose
   * route answers with messages we wrote for it (the review email voice check, the pause and keep rules) reads it,
   * through `ownWords`.
   */
  serverMessage?: string;
  constructor(code: ErrorCode | 'offline', status: number, message: string, field?: string, serverMessage?: string) {
    super(message);
    this.code = code;
    this.status = status;
    if (field !== undefined) this.field = field;
    if (serverMessage !== undefined) this.serverMessage = serverMessage;
  }
  get offline() {
    return this.code === 'offline';
  }
}

type Fetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
let transport: Fetch = (i, init) => fetch(i, init);
let tenantId: string | null = null;

export function setTransport(f: Fetch): void {
  transport = f;
}
export function getTransport(): Fetch {
  return transport;
}
export function setTenant(id: string | null): void {
  tenantId = id;
}
export function currentTenant(): string | null {
  return tenantId;
}

export function pathFor<K extends RouteId>(id: K, params: Record<string, string | number> = {}, query?: Record<string, unknown>): string {
  const route = clientRoutes[id];
  const values: Record<string, string | number> = { ...params };
  if (route.path.includes(':t')) {
    if (!tenantId) throw new ApiError('unauthenticated', 401, ERROR_CODES.unauthenticated.message);
    values.t = tenantId;
  }
  let path = buildPath(route.path, values);
  if (query) {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    const s = q.toString();
    if (s) path += `?${s}`;
  }
  return path;
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export async function call<K extends RouteId>(id: K, input: CallInput<K> = {}): Promise<Res<K>> {
  const route = clientRoutes[id];
  const path = pathFor(id, (input.params ?? {}) as Record<string, string>, input.query as Record<string, unknown> | undefined);
  const headers: Record<string, string> = { accept: 'application/json' };
  let body: BodyInit | undefined;
  if (route.requestFormat === 'json') {
    const parsed = route.body.safeParse(input.body);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      throw new ApiError('validation_failed', 422, ERROR_CODES.validation_failed.message, first?.path.map(String).join('.'));
    }
    headers['content-type'] = 'application/json';
    body = JSON.stringify(parsed.data);
  }
  if (MUTATING.has(route.method)) headers[CSRF_HEADER] = CSRF_HEADER_VALUE;

  let res: Response;
  try {
    res = await transport(path, { method: route.method, headers, body, credentials: 'same-origin', signal: input.signal });
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e;
    throw new ApiError('offline', 0, 'offline');
  }

  const text = await res.text();
  let json: unknown = undefined;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      throw new ApiError('internal', res.status, ERROR_CODES.internal.message);
    }
  }
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string; field?: string } } | undefined)?.error;
    const code = (err?.code && err.code in ERROR_CODES ? err.code : res.status >= 500 ? 'internal' : 'bad_request') as ErrorCode;
    const own = typeof err?.message === 'string' && err.message !== ERROR_CODES[code].message ? err.message.slice(0, 300) : undefined;
    throw new ApiError(code, res.status, ERROR_CODES[code].message, err?.field, own);
  }
  const out = route.response.safeParse(json);
  if (!out.success) {
    // The server answered with something outside its contract. Treat it as our fault, never render it.
    console.error(`[api] ${id} response failed its contract`, out.error.issues.slice(0, 3));
    throw new ApiError('internal', 500, ERROR_CODES.internal.message);
  }
  return out.data as Res<K>;
}

/**
 * The route's own refusal words for 409 and 422 answers, else the contract message. Use only on routes whose server
 * messages are written for the client (they're voice checked with the handler's tests).
 */
export function ownWords(e: unknown, offlineText: string): string {
  if (e instanceof ApiError && !e.offline && (e.status === 409 || e.status === 422) && e.serverMessage) return e.serverMessage;
  return errorText(e, offlineText);
}

/** Plain words for an error, for toasts and inline errors. */
export function errorText(e: unknown, offlineText: string): string {
  if (e instanceof ApiError) return e.offline ? offlineText : e.message;
  return ERROR_CODES.internal.message;
}

/** Client-generated idempotency key: a retried send never makes two (docs/55 E13). */
export function idemKey(): string {
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 26);
}
